import {
  List,
  Create,
  TextInput,
  PasswordInput,
  SelectInput,
  ReferenceInput,
  required,
  minLength,
  usePermissions,
  useListContext,
  downloadCSV,
  SimpleForm,
} from "react-admin";
import { unparse as convertToCSV } from 'papaparse';
import jsonExport from 'jsonexport/dist';

// Import components
import ListActions from "./components/actions-list";
import {
  UsersFilter,
  UsersDataGrid,
  UserDynamicFields
} from "./components/users";
import { TablePagination } from "@mui/material";

// Custom exporter function
const customExporter = (records) => {
  const csvData = records.map(record => ({
    Name: record.name,
    PhoneNumber: record.phonenumber ? `'${record.phonenumber}` : "",
    Location: record._state?.name,
    City: record._city?.name,
    CreatedAt: record.created_at ? new Date(record.created_at).toLocaleDateString() : "",
    UpdatedAt: record.updated_at ? new Date(record.updated_at).toLocaleDateString() : "",
    Type: record.type,
    Gender: record.gender,
    SubscribedAt: record.subscribed_at ? new Date(record.subscribed_at).toLocaleDateString() : "",
    DOB: record.dob ? new Date(record.dob).toLocaleDateString() : "",
    CooperativeType: record.cooperativeType,
    NoOfMembers: record.NoOfMembers,
    IsSubscribed: record.isSubscribed,
    IsVerified: record.isVerified,
  }));

  const csv = convertToCSV(csvData);
  jsonExport(csvData, { rowDelimiter: ',' }, (err, csv) => {
    if (err) {
      console.error(err);
      return;
    }
    const bom = '\uFEFF';
    downloadCSV(bom + csv, 'orders_export');
  });
};

// Custom pagination component
const CustomPagination = (props) => {
  const { total, perPage, page, setPerPage, setPage } = useListContext();

  const handleChangeRowsPerPage = (event) => {
    setPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  return (
    <TablePagination
      count={total}
      page={page - 1}
      rowsPerPage={perPage}
      onPageChange={(event, newPage) => setPage(newPage + 1)}
      onRowsPerPageChange={handleChangeRowsPerPage}
      rowsPerPageOptions={[10, 25, 50, 100]}
      component="div"
    />
  );
};

// Users List component
export const UsersList = (props) => {
  const { permanentFilter, ...rest } = props;
  return (
    <List
      {...rest}
      storeKey={false}
      filters={<UsersFilter />}
      actions={<ListActions filters={UsersFilter} />}
      pagination={<CustomPagination />}
      exporter={customExporter}
      perPage={25}
      filter={permanentFilter}
    >
      <UsersDataGrid />
    </List>
  );
};

// Phone number normalization function
const normalizeSudanesePhone = (input) => {
  const cleaned = input.replace(/\D/g, "");

  if (/^(0[19]\d{8})$/.test(cleaned)) {
    return `+249${cleaned.slice(1)}`;
  }

  if (/^(?:\+?249\d{9})$/.test(input)) {
    return input.startsWith("+") ? input : `+${input}`;
  }

  return input;
};

// User Create component
export const UserCreate = (props) => {
  const { permissions } = usePermissions();
  const canEdit = permissions === "admin" || permissions === "operater";

  const transform = (data) => {
    console.log("Original form data:", data);

    // Map frontend types to database types
    let type = data.type;
    let cooperativeType = data.cooperativeType;
    
    // Handle cooperative type mapping
    if (data.type === 'agriculture_cooperative') {
      type = 'cooperative';
      cooperativeType = 'agriculture';
    } else if (data.type === 'consumer_cooperative') {
      type = 'cooperative';
      cooperativeType = 'consumer';
    }

    // Prepare base transformed data
    const transformed = {
      phonenumber: normalizeSudanesePhone(data.phonenumber),
      name: data.name,
      password: data.password,
      location: Number(data.location),
      city: data.city ? Number(data.city) : null,
      type: type,
      // Include cooperativeType if it's a cooperative
      ...(type === 'cooperative' && cooperativeType && {
        cooperativeType: cooperativeType,
      }),
      // Include fields for farmer and buyer
      ...(type === 'farmer' || type === 'buyer') && {
        gender: data.gender,
        dob: data.dob ? new Date(data.dob).toISOString().split("T")[0] : undefined,
      },
      // Include subcategories for farmer, buyer, and cooperative
      ...((type === 'farmer' || type === 'buyer' || type === 'cooperative') && data.subCategory_id) && {
        subCategory_id: data.subCategory_id?.map(Number) || undefined,
      },
      // Include services for service provider
      ...(type === 'service_provider' && data.service_id) && {
        service_id: data.service_id?.map(Number) || undefined,
      },
      // Include NoOfMembers for cooperative
      ...(type === 'cooperative' && data.NoOfMembers) && {
        NoOfMembers: Number(data.NoOfMembers),
      },
    };

    console.log("Transformed data:", transformed);
    return transformed;
  };

  return canEdit ? (
    <Create {...props} transform={transform}>
      <SimpleForm>
        <TextInput
          source="phonenumber"
          label="Phone Number"
          validate={[required()]}
        />
        <TextInput source="name" label="Name" validate={[required()]} />
        <PasswordInput
          source="password"
          label="Password"
          validate={[required(), minLength(6)]}
        />
        <SelectInput
          source="type"
          label="User Type"
          choices={[
            { id: "farmer", name: "مزارع (Farmer)" },
            { id: "agriculture_cooperative", name: "تعاونية زراعية إنتاجية" },
            { id: "consumer_cooperative", name: "تعاونية إستهلاكية" },
            { id: "service_provider", name: "شركة (Service Provider)" },
            { id: "buyer", name: "مشتري (Buyer)" },
            { id: "marketer", name: "Marketer" },
            { id: "supplier", name: "Supplier" },
          ]}
          validate={[required()]}
        />
        <ReferenceInput
          source="location"
          reference="state"
          label="State"
          validate={[required()]}
        >
          <SelectInput optionText="name" />
        </ReferenceInput>
        
        <ReferenceInput
          source="city"
          reference="city"
          label="City"
        >
          <SelectInput optionText="name" />
        </ReferenceInput>

        <UserDynamicFields />
      </SimpleForm>
    </Create>
  ) : undefined;
};